import axiosAuthAPI from './axiosAuthAPI';
import axiosAPI from './axiosAPI';

class SubscriptionService {
  /**
   * Gets organization details by code (uses the lookup endpoint from the API docs).
   * @param {string} code - The organization code to validate
   * @returns {Promise<Object>} - Information about the organization
   */
  async getOrganizationByCode(code: string) {
    try {
      // For logged-in users, use the protected endpoint
      if (axiosAuthAPI.defaults.headers.common['Authorization']) {
        const response = await axiosAuthAPI.get(
          `/api/v1/student/subscriptions/organization/lookup/${code}`
        );
        return response.data;
      }
      // For non-logged in users (during registration), use the public endpoint
      else {
        const response = await axiosAPI.get(
          `/api/v1/student/registration/validate-code/${code}`
        );
        return response.data;
      }
    } catch (error) {
      console.error('Organization lookup error:', error);
      throw error;
    }
  }

  /**
   * Gets organization code by organization ID.
   * @param {number} organizationId - The organization ID
   * @returns {Promise<{friendly_code: string}>} - Object containing the friendly code
   */
  async getOrganizationCode(organizationId: number) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/organizations/${organizationId}/code`
      );
      return response.data;
    } catch (error) {
      console.error('Organization code lookup error:', error);
      throw error;
    }
  }

  /**
   * Gets organization code from a subscription ID.
   * @param {string} subscriptionId - The Stripe subscription ID
   * @returns {Promise<{friendly_code: string}>} - Object containing the organization friendly code
   */
  async getSubscriptionOrganizationCode(subscriptionId: string) {
    try {
      // This endpoint needs to be implemented in the backend
      const response = await axiosAuthAPI.get(
        `/api/v1/student/subscriptions/${subscriptionId}/organization-code`
      );
      return response.data;
    } catch (error) {
      console.error('Subscription organization code lookup error:', error);
      throw error;
    }
  }

  /**
   * Gets the current user's organization code.
   * This is the most direct way to get an organization code for subscription creation.
   * @returns {Promise<{organization_code: string, organization_name: string, organization_id: number, subscription_amount: number}>}
   */
  async getCurrentUserOrganizationCode() {
    try {
      const response = await axiosAuthAPI.get(
        '/api/v1/student/subscriptions/organization/code'
      );
      return response.data;
    } catch (error) {
      console.error('User organization code lookup error:', error);
      throw error;
    }
  }

  /**
   * Checks if an email is available for registration.
   * @param {string} email - The email to check
   * @returns {Promise<{available: boolean}>} - Whether the email is available
   */
  async checkEmailAvailability(email: string) {
    try {
      const response = await axiosAPI.post(
        '/api/v1/student/registration/check-email',
        { email }
      );
      return response.data;
    } catch (error) {
      console.error('Email check error:', error);
      throw error;
    }
  }

  /**
   * Gets available license types for registration.
   * @returns {Promise<{license_types: Array}>} - Available license types
   */
  async getLicenseTypes() {
    try {
      const response = await axiosAPI.get(
        '/api/v1/student/registration/license-types'
      );
      return response.data;
    } catch (error) {
      console.error('License types error:', error);
      throw error;
    }
  }

  /**
   * Registers a new user.
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} - Registration result
   */
  async registerUser(userData: {
    friendly_code: string;
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    dni: string;
    license_type: string;
  }) {
    try {
      const response = await axiosAPI.post(
        '/api/v1/student/registration/register',
        userData
      );
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  /**
   * Creates a new subscription.
   * @param {string} friendlyCode - Organization code
   * @param {string} paymentMethodId - Stripe payment method ID
   * @returns {Promise<Object>} - Subscription creation result
   */
  async createSubscription(friendlyCode: string, paymentMethodId: string) {
    try {
      const response = await axiosAuthAPI.post(
        '/api/v1/student/subscriptions/create',
        {
          friendly_code: friendlyCode,
          payment_method_id: paymentMethodId,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Subscription creation error:', error);
      throw error;
    }
  }

  /**
   * Gets the current subscription status.
   * @returns {Promise<Object>} - Subscription status information including:
   * - has_subscription: Whether the user has a subscription
   * - subscription_id: The Stripe subscription ID
   * - status: The subscription status (active, canceled, etc.)
   * - subscription_type: The type of subscription
   * - organization_id: The organization ID
   * - organization_name: The organization name
   * - current_period_end: When the current billing period ends
   * - subscription_amount: The subscription amount
   * - payment_method_last4: Last 4 digits of payment method
   * - created_at: When the subscription was created
   * - cancelled_at: When the cancellation was requested (null if not canceled)
   * - cancel_at_period_end: Whether cancellation is delayed until period end
   * - access_until: Date until which the user will have access (null if not canceled or immediate cancel)
   */
  async getSubscriptionStatus() {
    try {
      const response = await axiosAuthAPI.get(
        '/api/v1/student/subscriptions/status'
      );
      return response.data;
    } catch (error) {
      console.error('Subscription status error:', error);
      throw error;
    }
  }

  /**
   * Cancels the current subscription.
   * @param {boolean} cancelAtPeriodEnd - Whether to cancel at the end of the billing period
   * @returns {Promise<Object>} - Cancellation result with message, subscription_id, current_period_end, cancel_at_period_end, and access_until
   */
  async cancelSubscription(cancelAtPeriodEnd = true) {
    try {
      const response = await axiosAuthAPI.post(
        '/api/v1/student/subscriptions/cancel',
        {
          cancel_at_period_end: cancelAtPeriodEnd,
        }
      );

      // The enhanced response now includes:
      // - message: A human-readable message about the cancellation
      // - subscription_id: The Stripe subscription ID
      // - current_period_end: When the current billing period ends
      // - cancel_at_period_end: Whether cancellation is delayed until period end
      // - access_until: Date until which the user will have access (if canceling at period end)

      return response.data;
    } catch (error) {
      console.error('Subscription cancellation error:', error);
      throw error;
    }
  }

  /**
   * Updates the payment method for the current subscription.
   * @param {string} paymentMethodId - Stripe payment method ID
   * @returns {Promise<Object>} - Update result
   */
  async updatePaymentMethod(paymentMethodId: string) {
    try {
      const response = await axiosAuthAPI.post(
        '/api/v1/student/subscriptions/update-payment',
        {
          payment_method_id: paymentMethodId,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Payment method update error:', error);
      throw error;
    }
  }
}

export default new SubscriptionService();
